'use strict';

const dates = require('util/date');

const groupBy = require('lodash/groupBy');
const map = require('lodash/map');
const minBy = require('lodash/minBy');
const maxBy = require('lodash/maxBy');
const uniqWith = require('lodash/uniqWith');
const orderBy = require('lodash/orderBy');
const uniq = require('lodash/uniq');
const min = require('lodash/min');
const max = require('lodash/max');
const forEach = require('lodash/forEach')

// We'll be using really crude caching here. We could use memoize but why get a fat function
// when you can just do the same in vanilla JS with a few lines
const cache = {};

module.exports = ['$http', 'appConfig', ($http, appConfig) => {

    // Process the responses to return a single feed with all MOS OnSale Dates
    function loadPerformances() {
        return $http
            .get(appConfig.eventFeedURL, { cache: true, withCredentials: true })
            .then(filterOldEvents)
            .then(results => {
                forEach(results, perf => {
                    perf.on_sale_dates = [
                        {
                            on_sale_date: perf.on_sale_date,
                            on_sale_date_moment: dates.moment(perf.on_sale_date),
                        }
                    ];
                });

                return results;
            });
    }

    function filterOldEvents(response) {
        const hour = dates.moment().startOf('hour');

        return response.data.filter(performance => {
            return dates.moment(performance.start_date).isAfter(hour);
        });
    }

    // Get performances is just an alias
    function getPerformances() {
        if ('getPerformances' in cache) {
            return cache.getPerformances;
        }

        cache.getPerformances = loadPerformances().then(response => {
            // Do some management
            let performances = map(response, performance => {
                // Momentise some dates for ease of use after
                performance.start_date_moment = dates.moment(performance.start_date);
                performance.end_date_moment = dates.moment(performance.end_date);
                performance.on_sale_date_moment = dates.moment(performance.on_sale_date);
                performance.off_sale_date_moment = dates.moment(performance.off_sale_date);

                // Grab where price is present
                performance.pricing = performance.pricing || [];
                let pricing = performance.pricing.filter(item => parseFloat(item.price));

                // We only need unique price values
                pricing = uniqWith(pricing, (a, b) => {
                    return parseFloat(a.price) === parseFloat(b.price);
                });

                // Now, just grab and assign the price values
                performance.pricing = map(pricing, item => parseFloat(item.price));

                // Grab min & max pricing
                performance.min_price = min(performance.pricing) || 0;
                performance.max_price = max(performance.pricing) || 0;

                // Return the updated item
                return performance;
            });

            // Sort by start date & return
            return orderBy(performances, item => item.start_date_moment.valueOf());
        });

        return cache.getPerformances;
    };

    // Get events (basically performances grouped)
    function getEvents() {
        if ('getEvents' in cache) {
            return cache.getEvents;
        }

        cache.getEvents = getPerformances().then(response => {
            // Group by prod season number
            let events = groupBy(response, 'prod_season_no');

            // Run through group and generate a events
            events = map(events, performances => {
                let performance = performances[0];

                // Grab the first performance by start date
                const firstPerformance = minBy(performances, 'start_date');

                // Grab the last performance by end date
                const lastPerformance = maxBy(performances, 'end_date');

                // Grab the last performance by on sale date
                const onSalePerformance = minBy(performances, 'on_sale_date');

                // Grab the last performance by off sale date
                const offSalePerformance = maxBy(performances, 'off_sale_date');

                // Grab facets from performances
                let facets = map(performances, item => item.facets);

                // Merge the facets
                facets = [].concat.apply([], facets);

                // Make then unique
                facets = uniqWith(facets, (a, b) => {
                    return (
                        a.category === b.category &&
                        a.keyword === b.keyword
                    );
                });

                facets = facets.reduce((carry, facet) => {
                    carry.push({
                        keyword: facet.keyword.trim(),
                        category: facet.category,
                    });
                    return carry;
                }, []);

                // Grab pricing from performances
                let pricing = map(performances, item => item.pricing);

                // Merge the pricing
                pricing = [].concat.apply([], pricing);

                // Make then unique
                pricing = uniq(pricing);

                // Grab min & max prices
                const minPrice = min(pricing);
                const maxPrice = max(pricing);

                // Sum the availability
                const availability = performances.reduce((carry, item) => {
                    return carry += parseInt(item.availability, 10);
                }, 0);

                // Return a new event item
                return {
                    // These all come from a performance. Doesn't matter which one.
                    title: performance.title,
                    prod_season_no: performance.prod_season_no,
                    event_book_url: performance.event_book_url,
                    book_url: performance.book_url,
                    event_url: performance.event_url,
                    prefix: performance.prefix,
                    suffix: performance.suffix,
                    booking_status: performance.booking_status,
                    custom_booking_message: performance.custom_booking_message,
                    booking_enabled: performance.booking_enabled,
                    presenter: performance.presenter,

                    image_url: performance.image_url,

                    // Assign the performances
                    performances: performances,

                    // Assign the facets
                    facets: facets,

                    // Assign the pricing
                    pricing: pricing,
                    min_price: minPrice,
                    max_price: maxPrice,

                    // Assign the availability
                    availability: availability,

                    // Store the dates as is...
                    start_date: firstPerformance.start_date,
                    end_date: lastPerformance.end_date,
                    on_sale_date: onSalePerformance.on_sale_date,
                    off_sale_date: offSalePerformance.off_sale_date,

                    date_description: onSalePerformance.date_description,
                    price_description: onSalePerformance.price_description,

                    // ...and as moment objects
                    start_date_moment: firstPerformance.start_date_moment,
                    end_date_moment: lastPerformance.end_date_moment,
                    on_sale_date_moment: onSalePerformance.on_sale_date_moment,
                    off_sale_date_moment: offSalePerformance.off_sale_date_moment,

                    subscriptions: firstPerformance.subscriptions,
                };
            });

            // Sort by start date & return
            return orderBy(events, item => item.start_date_moment.valueOf());
        });

        return cache.getEvents;
    }

    return {
        getPerformances: getPerformances,
        getEvents: getEvents,
    }
}];
